'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';

export default function HeroSection() {
  const womanRef = useRef<HTMLDivElement>(null);
  const bgTextRef = useRef<HTMLHeadingElement>(null);
  const rippleRef = useRef<HTMLDivElement>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let gsap: typeof import('gsap').gsap;
    let cleanupFn: (() => void) | undefined;

    const init = async () => {
      const g = await import('gsap');
      gsap = g.gsap;

      // Floating animation
      gsap.to(womanRef.current, {
        y: -18,
        duration: 5,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut'
      });

      // Background text animation
      gsap.to(bgTextRef.current, {
        x: 40,
        duration: 25,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut'
      });

      // Ripple click effect
      const handleClick = (e: MouseEvent) => {
        const ripple = document.createElement('div');
        ripple.className = 'ripple';
        ripple.style.left = `${e.clientX}px`;
        ripple.style.top = `${e.clientY}px`;

        rippleRef.current?.appendChild(ripple);

        gsap.fromTo(
          ripple,
          { scale: 0, opacity: 0.25 },
          {
            scale: 6,
            opacity: 0,
            duration: 1.2,
            ease: 'power2.out',
            onComplete: () => ripple.remove()
          }
        );
      };

      window.addEventListener('click', handleClick);

      cleanupFn = () => {
        window.removeEventListener('click', handleClick);
      };
    };

    init();

    return () => cleanupFn?.();
  }, []);

  const exploreLinks = [
    { label: "About Us", href: "/about" },
    { label: "Programs", href: "/programs" },
    { label: "Contact", href: "/contact" }
  ];

  const servicesLinks = [
    { label: "Acupuncture", href: "/therapies/acupuncture" },
    { label: "Cupping Therapy", href: "/therapies/cupping-therapy" },
    { label: "Gua Sha", href: "/therapies/gua-sha-therapy" },
    { label: "Yoga Therapy", href: "/yoga/yoga-therapy" },
    { label: "Bach Remedies", href: "/therapies/bach-remedies" },
    { label: "Mudra Therapy", href: "/therapies/mudra-varma-therapy" },
    { label: "Reiki Healing", href: "/therapies/reiki-healing" },
    { label: "Kundalini Yoga", href: "/yoga/kundalini-yoga" },
    { label: "Kayakalpa Yoga", href: "/yoga/kayakalpa-yoga" }
  ];

  return (
    <section data-component-id="src/app/components/HeroSection.tsx:92:4" data-component-path="src/app/components/HeroSection.tsx" data-component-line="92" data-component-end-line="209" data-component-file="HeroSection.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22relative%20min-h-screen%20flex%20items-center%20justify-center%20overflow-hidden%22%7D"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{
        background: 'radial-gradient(circle at top left, #314d42, #1C2B24 55%, #152019)'
      }}
    >
      {/* Ripple Layer */}
      <div data-component-id="src/app/components/HeroSection.tsx:99:6" data-component-path="src/app/components/HeroSection.tsx" data-component-line="99" data-component-end-line="99" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22fixed%20inset-0%20pointer-events-none%20z-50%22%7D" ref={rippleRef} className="fixed inset-0 pointer-events-none z-50" />

      {/* Background Text */}
      <h1 data-component-id="src/app/components/HeroSection.tsx:102:6" data-component-path="src/app/components/HeroSection.tsx" data-component-line="102" data-component-end-line="108" data-component-file="HeroSection.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22absolute%20text-%5B120px%5D%20md%3Atext-%5B180px%5D%20lg%3Atext-%5B240px%5D%20text-white%2F%5B0.03%5D%20font-serif%20leading-%5B0.8%5D%20tracking-tight%20select-none%22%2C%22textContent%22%3A%22PRANAVA%20WELLNESS%22%7D"
        ref={bgTextRef}
        className="absolute text-[120px] md:text-[180px] lg:text-[240px] text-white/[0.03] font-serif leading-[0.8] tracking-tight select-none"
      >
        PRANAVA <br data-component-id="src/app/components/HeroSection.tsx:106:16" data-component-path="src/app/components/HeroSection.tsx" data-component-line="106" data-component-end-line="106" data-component-file="HeroSection.tsx" data-component-name="br" data-component-content="%7B%22elementName%22%3A%22br%22%7D" />
        WELLNESS
      </h1>

      {/* MAIN GRID */}
      <div data-component-id="src/app/components/HeroSection.tsx:111:6" data-component-path="src/app/components/HeroSection.tsx" data-component-line="111" data-component-end-line="196" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20z-10%20grid%20md%3Agrid-cols-2%20gap-10%20items-center%20px-6%20md%3Apx-20%20w-full%20max-w-7xl%22%7D" className="relative z-10 grid md:grid-cols-2 gap-10 items-center px-6 md:px-20 w-full max-w-7xl">

        {/* LEFT - IMAGE */}
        <div data-component-id="src/app/components/HeroSection.tsx:114:8" data-component-path="src/app/components/HeroSection.tsx" data-component-line="114" data-component-end-line="131" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20justify-center%22%7D" className="flex justify-center">
          <div data-component-id="src/app/components/HeroSection.tsx:115:10" data-component-path="src/app/components/HeroSection.tsx" data-component-line="115" data-component-end-line="130" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-%5B280px%5D%20md%3Aw-%5B450px%5D%20lg%3Aw-%5B520px%5D%20aspect-%5B3%2F4%5D%22%7D"
            ref={womanRef}
            className="w-[280px] md:w-[450px] lg:w-[520px] aspect-[3/4]"
          >
            <AppImage data-component-id="src/app/components/HeroSection.tsx:119:12" data-component-path="src/app/components/HeroSection.tsx" data-component-line="119" data-component-end-line="129" data-component-file="HeroSection.tsx" data-component-name="AppImage" data-component-content="%7B%22elementName%22%3A%22AppImage%22%2C%22src%22%3A%22%2Fassets%2Fimages%2Fyoga-girl-banner.png%22%2C%22alt%22%3A%22Yoga%22%7D"
              src="/assets/images/yoga-girl-banner.png"
              alt="Yoga"
              width={600}
              height={800}
              priority
              onLoadingComplete={() => setLoaded(true)}
              className={`w-full h-full object-contain transition-opacity duration-500 ${
                loaded ? 'opacity-100' : 'opacity-0'
              }`}
            />
          </div>
        </div>

        {/* RIGHT - CONTENT */}
        <div data-component-id="src/app/components/HeroSection.tsx:134:8" data-component-path="src/app/components/HeroSection.tsx" data-component-line="134" data-component-end-line="195" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%22%7D" className="relative">

          {/* LOGO */}
          <div data-component-id="src/app/components/HeroSection.tsx:137:10" data-component-path="src/app/components/HeroSection.tsx" data-component-line="137" data-component-end-line="143" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-6%22%7D" className="mb-6">
            <img data-component-id="src/app/components/HeroSection.tsx:138:12" data-component-path="src/app/components/HeroSection.tsx" data-component-line="138" data-component-end-line="142" data-component-file="HeroSection.tsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22src%22%3A%22%2Fassets%2Fimages%2Ffavicon-pranava.png%22%2C%22alt%22%3A%22Brand%20Logo%22%2C%22className%22%3A%22h-16%20md%3Ah-20%20w-auto%20opacity-90%22%7D"
              src="/assets/images/favicon-pranava.png"
              alt="Brand Logo"
              className="h-16 md:h-20 w-auto opacity-90"
            />
          </div>

          {/* HEADING */}
          <h2 data-component-id="src/app/components/HeroSection.tsx:146:10" data-component-path="src/app/components/HeroSection.tsx" data-component-line="146" data-component-end-line="149" data-component-file="HeroSection.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-4xl%20md%3Atext-5xl%20lg%3Atext-5xl%20text-white%20mb-4%20leading-tight%20font-light%22%2C%22textContent%22%3A%22Natural%20Healing%22%7D" className="text-4xl md:text-5xl lg:text-5xl text-white mb-4 leading-tight font-light">
            Natural Healing <br data-component-id="src/app/components/HeroSection.tsx:147:28" data-component-path="src/app/components/HeroSection.tsx" data-component-line="147" data-component-end-line="147" data-component-file="HeroSection.tsx" data-component-name="br" data-component-content="%7B%22elementName%22%3A%22br%22%7D" />
            <span data-component-id="src/app/components/HeroSection.tsx:148:12" data-component-path="src/app/components/HeroSection.tsx" data-component-line="148" data-component-end-line="148" data-component-file="HeroSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22italic%20text-%5B%23d8c7a3%5D%22%2C%22textContent%22%3A%22for%20Complete%20Balance%22%7D" className="italic text-[#d8c7a3]">for Complete Balance</span>
          </h2>

          {/* DESCRIPTION */}
          <p data-component-id="src/app/components/HeroSection.tsx:152:10" data-component-path="src/app/components/HeroSection.tsx" data-component-line="152" data-component-end-line="155" data-component-file="HeroSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-white%2F70%20mb-10%20max-w-lg%20text-sm%20md%3Atext-%5B0.92rem%5D%20leading-relaxed%22%2C%22textContent%22%3A%22Acupuncture%2C%20Yoga%2C%20Reiki%2C%20Bach%20Flower%20Remedies%20and%20holistic%20therapies%5Cn%20%20%20%20%20%20%20%20%20%20%20%20crafted%20to%20restore%20your%20body%2C%20mind%20and%20inner%20energy%20naturally.%22%7D" className="text-white/70 mb-10 max-w-lg text-sm md:text-[0.92rem] leading-relaxed">
            Acupuncture, Yoga, Reiki, Bach Flower Remedies and holistic therapies
            crafted to restore your body, mind and inner energy naturally.
          </p>

          {/* EXPLORE LINKS */}
          <div data-component-id="src/app/components/HeroSection.tsx:158:10" data-component-path="src/app/components/HeroSection.tsx" data-component-line="158" data-component-end-line="174" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-12%22%7D" className="mb-12">
            <p data-component-id="src/app/components/HeroSection.tsx:159:12" data-component-path="src/app/components/HeroSection.tsx" data-component-line="159" data-component-end-line="161" data-component-file="HeroSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-white%2F40%20text-xs%20uppercase%20tracking-%5B0.35em%5D%20mb-5%22%2C%22textContent%22%3A%22Explore%22%7D" className="text-white/40 text-xs uppercase tracking-[0.35em] mb-5">
              Explore
            </p>

            <div data-component-id="src/app/components/HeroSection.tsx:163:12" data-component-path="src/app/components/HeroSection.tsx" data-component-line="163" data-component-end-line="173" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-wrap%20gap-4%22%7D" className="flex flex-wrap gap-4">
              {exploreLinks.map((item) => (
                <Link data-component-id="src/app/components/HeroSection.tsx:165:16" data-component-path="src/app/components/HeroSection.tsx" data-component-line="165" data-component-end-line="171" data-component-file="HeroSection.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22className%22%3A%22backdrop-blur-xl%20bg-white%2F10%20border%20border-white%2F20%20px-6%20py-3%20rounded-full%20text-white%20text-sm%20transition-all%20duration-300%20hover%3Abg-white%2F20%20hover%3A-translate-y-1%20shadow-%5B0_10px_40px_rgba(255%2C255%2C255%2C0.08)%5D%22%7D"
                  key={item.label}
                  href={item.href}
                  className="backdrop-blur-xl bg-white/10 border border-white/20 px-6 py-3 rounded-full text-white text-sm transition-all duration-300 hover:bg-white/20 hover:-translate-y-1 shadow-[0_10px_40px_rgba(255,255,255,0.08)]"
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>

          {/* SERVICES LINKS */}
          <div data-component-id="src/app/components/HeroSection.tsx:177:10" data-component-path="src/app/components/HeroSection.tsx" data-component-line="177" data-component-end-line="193" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
            <p data-component-id="src/app/components/HeroSection.tsx:178:12" data-component-path="src/app/components/HeroSection.tsx" data-component-line="178" data-component-end-line="180" data-component-file="HeroSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-white%2F40%20text-xs%20uppercase%20tracking-%5B0.35em%5D%20mb-5%22%2C%22textContent%22%3A%22Healing%20Services%22%7D" className="text-white/40 text-xs uppercase tracking-[0.35em] mb-5">
              Healing Services
            </p>

            <div data-component-id="src/app/components/HeroSection.tsx:182:12" data-component-path="src/app/components/HeroSection.tsx" data-component-line="182" data-component-end-line="192" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-wrap%20gap-4%20max-w-xl%22%7D" className="flex flex-wrap gap-4 max-w-xl">
              {servicesLinks.map((service) => (
                <Link data-component-id="src/app/components/HeroSection.tsx:184:16" data-component-path="src/app/components/HeroSection.tsx" data-component-line="184" data-component-end-line="190" data-component-file="HeroSection.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22className%22%3A%22backdrop-blur-2xl%20bg-white%2F10%20border%20border-white%2F20%20text-white%2F90%20rounded-2xl%20px-5%20py-3%20text-sm%20transition-all%20duration-300%20hover%3Abg-white%2F20%20hover%3A-translate-y-1%20shadow-%5B0_10px_40px_rgba(255%2C255%2C255%2C0.06)%5D%22%7D"
                  key={service.label}
                  href={service.href}
                  className="backdrop-blur-2xl bg-white/10 border border-white/20 text-white/90 rounded-2xl px-5 py-3 text-sm transition-all duration-300 hover:bg-white/20 hover:-translate-y-1 shadow-[0_10px_40px_rgba(255,255,255,0.06)]"
                >
                  {service.label}
                </Link>
              ))}
            </div>
          </div>

        </div>
      </div>

      {/* Ripple Style */}
      <style data-component-id="src/app/components/HeroSection.tsx:199:6" data-component-path="src/app/components/HeroSection.tsx" data-component-line="199" data-component-end-line="208" data-component-file="HeroSection.tsx" data-component-name="style" data-component-content="%7B%22elementName%22%3A%22style%22%7D" jsx>{`
        .ripple {
          position: absolute;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: rgba(200, 169, 110, 0.25);
          transform: translate(-50%, -50%);
        }
      `}</style>
    </section>
  );
}